"use client";

import React from 'react';
import { cn } from '@/lib/utils';
import { DropdownMenuItem } from './DropdownMenuItem';
import type { DropdownMenuItemProps } from './types';

export interface DropdownMenuFooterProps {
  /** Rows to render inside the footer. Omit to render `children` instead. */
  items?: DropdownMenuItemProps[];
  className?: string;
  children?: React.ReactNode;
}

/**
 * Guided-theme menu footer.
 *
 * A tinted, hairline-topped block pinned to the bottom of a popover. Actions
 * that live here (Lock database, Delete Board, Move to Trash) read as a
 * different class of action before their label is even read — which is the
 * whole point of moving them out of the main stack.
 *
 * Rows sit at 32px rather than the 38px used in the body: a footer action is
 * never the reason the menu was opened.
 */
export function DropdownMenuFooter({
  items,
  className,
  children,
}: DropdownMenuFooterProps) {
  return (
    <div
      className={cn(
        "mt-1 p-1 rounded-b-[10px] border-t border-component",
        "bg-[color-mix(in_srgb,var(--hover-bg),transparent_60%)]",
        "[&_button]:min-h-[32px]",
        className
      )}
    >
      {items?.map((item, index) => (
        <DropdownMenuItem key={item.id ?? index} {...item} />
      ))}
      {children}
    </div>
  );
}
